import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DEFAULT_INTERRUPTSOURCES, Idle, InterruptSource } from '@ng-idle/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { filter, finalize, map, take, takeUntil, tap } from 'rxjs/operators';

import { UserService } from '@main-client/src/app/user/user.service';

import { UserIdleTimeoutDialogComponent } from '@main-client/src/app/user-idle-timeout/user-idle-timeout-dialog/user-idle-timeout-dialog.component';

import { Tenant } from '@libs/src/tenant/tenant.interface';
import { IAppState } from '@main-client/src/app/app.state';
import { defaultAdminLockoutTimeMinutes } from '@main-client/src/app/settings/settings.helpers';

import isEmpty from 'lodash-es/isEmpty';

interface IdleWatcherConfig {
  idle: number;
  timeOut: number;
  interrupts: InterruptSource[];
}

export enum States {
  dialog = 'dialog',
  init = 'init',
}

export const oneMinuteInS = 60;
export const idleTimeInS = defaultAdminLockoutTimeMinutes * oneMinuteInS;
export const timeoutForDialogResponseInS = oneMinuteInS;
export const timeoutOffset = 1;

@Injectable()
export class UserIdleTimeoutService {
  loggedOut = new ReplaySubject<void>(1);
  state = new BehaviorSubject(States.init);
  countdown = new BehaviorSubject(timeoutForDialogResponseInS);
  idleTimeInS = this.store.select('tenant').pipe(
    filter((tenant: Tenant) => !isEmpty(tenant)),
    takeUntil(this.loggedOut),
    map(
      (tenant: Tenant) =>
        tenant.admin_lockout_time_minutes * oneMinuteInS || idleTimeInS,
    ),
  );

  constructor(
    public dialog: MatDialog,
    public idle: Idle,
    public store: Store<IAppState>,
    public userService: UserService,
  ) {
    this.initOnTimeoutWarning().subscribe();
    this.initOnTimeout().subscribe();
  }

  initOnTimeoutWarning() {
    return this.idle.onTimeoutWarning.pipe(
      takeUntil(this.loggedOut),
      filter(() => States.dialog === this.state.getValue()),
      tap((counterTime: number) =>
        this.countdown.next(counterTime - timeoutOffset),
      ),
    );
  }

  initOnTimeout() {
    return this.idle.onTimeout.pipe(
      takeUntil(this.loggedOut),
      tap(() => {
        if (States.init === this.state.getValue()) {
          return this.initDialogTimeout(this.countdown);
        }
        this.userService.logout();
        this.loggedOut.next();
        this.loggedOut.complete();
      }),
    );
  }

  initDialogTimeout(countdown: BehaviorSubject<number>) {
    this.openTimeoutDialog(countdown).subscribe();
    const idle = timeoutOffset;
    const timeOut = timeoutForDialogResponseInS;
    this.initIdleWatcher({ idle, timeOut, interrupts: [] });
  }

  openTimeoutDialog(countdown: BehaviorSubject<number>) {
    this.state.next(States.dialog);
    return this.dialog
      .open(UserIdleTimeoutDialogComponent, { data: { countdown } })
      .afterClosed()
      .pipe(
        tap((continueSession: boolean) => this.onDialogClose(continueSession)),
      );
  }

  onDialogClose(continueSession: boolean) {
    this.countdown.next(timeoutForDialogResponseInS);
    return continueSession ? this.initIdleTimeout() : this.idle.timeout();
  }

  initIdleWatcher({ idle, timeOut, interrupts }: IdleWatcherConfig) {
    this.destroyIdleWatcher();
    this.idle.setIdle(idle);
    this.idle.setTimeout(timeOut);
    this.idle.setInterrupts(interrupts);
    this.idle.watch();
  }

  destroyIdleWatcher() {
    this.idle.ngOnDestroy();
  }

  initIdleTimeout() {
    this.state.next(States.init);
    this.idleTimeInS
      .pipe(
        take(1),
        tap((idleTime: number) => {
          this.initIdleWatcher({
            idle: idleTime,
            timeOut: timeoutOffset,
            interrupts: DEFAULT_INTERRUPTSOURCES,
          });
        }),
      )
      .subscribe();
  }
}
